import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntitySettingsForm } from '@/components/entity-settings/types';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import type { PrimaryEntityType } from '@/lib/types';

export type EntitySettingsType = PrimaryEntityType | 'events' | string;

/**
 * Shared hook to manage entity settings (load, save, import) using denormalized novel data.
 * Handles architectural differences based on novel size.
 * Updated to use user-centric paths: /users/{userId}/novels/{novelId}
 */
export function useEntitySettingsManager(type: EntitySettingsType) {
  const { novel } = useNovel();
  const { user, backend } = useFirebase();
  const novelId = novel?.id;
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const categoryName = useMemo(() => {
    if (type.startsWith('codex:') && novel?.codexCategories) {
      const catId = type.replace('codex:', '');
      return novel.codexCategories[catId];
    }
    return undefined;
  }, [type, novel?.codexCategories]);

  const loadSettings = useCallback(async (id: string, updateForm: (data: EntitySettingsForm) => void) => {
    if (!backend || !id || !user || !novel.id) return;
    try {
      setIsLoading(true);

      const [ownerId, novelId] = id.includes(';') ? id.split(';') : [user.uid, id];
      const settings = await backend.entities.loadEntitySettings(
        ownerId,
        novelId,
        novel.storageLayout,
        type,
        categoryName
      );

      updateForm({ advancedSections: settings });
    } catch (error) {
      console.error(`Error loading ${type} settings:`, error);
      toast({
        title: t('common.error'),
        description: t('entity_form.common.save_failed_title'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [backend, type, categoryName, toast, user, novel, t]);

  const saveSettings = async (values: EntitySettingsForm) => {
    if (!backend || !novelId || !novel.id || !user) return;

    setIsSaving(true);
    try {
      const ownerId = novel.ownerId || user.uid;
      const sections = values.advancedSections.map((section, sIndex) => ({
        id: section.id,
        title: section.title,
        order: sIndex,
        fields: section.fields.map((field, fIndex) => ({
          id: field.id,
          label: field.label,
          type: field.type,
          placeholder: field.placeholder || '',
          order: fIndex,
          options: field.type === 'select' ? (field.options || []) : []
        }))
      }));

      await backend.entities.saveEntitySettings(
        ownerId,
        novelId,
        novel.storageLayout,
        type,
        sections
      );

      toast({
        title: t('common.success'),
        description: t('entity_settings.settings_saved'),
      });
    } catch (error) {
      console.error(`Error saving ${type} settings:`, error);
      toast({
        title: t('common.error'),
        description: t('entity_form.common.save_failed_title'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    loadSettings,
    saveSettings,
    isLoading,
    isSaving,
  };
}
